import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faExclamationCircle,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faPlus,
  faSearch,
  faInbox,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";
import "./BillDetail.css";

const BillDetail = ({ billId, onClose, onUpdate }) => {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedBill, setEditedBill] = useState({});
  const [editedItems, setEditedItems] = useState([]);
  const [newItem, setNewItem] = useState({
    item_name: "",
    quantity: "",
    unit_price: "",
    total_price: "",
    category: "",
  });
  const [showAddItem, setShowAddItem] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [zoomLevels, setZoomLevels] = useState({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showImages, setShowImages] = useState(window.innerWidth > 1024);
  const wrapperRefs = useRef({});

  const getZoom = (id) => zoomLevels[id] || 1;

  const zoomIn = (id) =>
    setZoomLevels((p) => ({ ...p, [id]: Math.min((p[id] || 1) + 0.25, 5) }));
  const zoomOut = (id) =>
    setZoomLevels((p) => ({ ...p, [id]: Math.max((p[id] || 1) - 0.25, 0.25) }));
  const zoomReset = (id) => {
    setZoomLevels((p) => ({ ...p, [id]: 1 }));
    const el = wrapperRefs.current[id];
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  };
  const panImage = (id, dx, dy) => {
    const el = wrapperRefs.current[id];
    if (el) {
      el.scrollBy({ left: dx, top: dy, behavior: "smooth" });
    }
  };

  const fetchBillDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getBillById(billId);
      setBill(data);
      setEditedBill(data);
      setEditedItems(data.items || []);
    } catch (err) {
      setError(err.message || "Failed to fetch bill details");
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => {
    if (billId) {
      fetchBillDetails();
    }
  }, [billId, fetchBillDetails]);

  const handleBillFieldChange = (field, value) => {
    setEditedBill((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemFieldChange = (index, field, value) => {
    const updatedItems = [...editedItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Auto-calculate total_price if quantity or unit_price changes (but allow manual override)
    if (field === "quantity" || field === "unit_price") {
      const quantity = parseFloat(
        field === "quantity" ? value : updatedItems[index].quantity,
      );
      const unitPrice = parseFloat(
        field === "unit_price" ? value : updatedItems[index].unit_price,
      );

      if (!isNaN(quantity) && !isNaN(unitPrice)) {
        updatedItems[index].total_price = (quantity * unitPrice).toFixed(2);
      }
    }

    setEditedItems(updatedItems);
  };

  const handleNewItemChange = (field, value) => {
    setNewItem((prev) => ({ ...prev, [field]: value }));

    // Only auto-calculate total_price if quantity or unit_price changes
    // and user hasn't manually set the total_price
    if (field === "quantity" || field === "unit_price") {
      const quantity = parseFloat(
        field === "quantity" ? value : newItem.quantity,
      );
      const unitPrice = parseFloat(
        field === "unit_price" ? value : newItem.unit_price,
      );

      if (!isNaN(quantity) && !isNaN(unitPrice)) {
        setNewItem((prev) => ({
          ...prev,
          total_price: (quantity * unitPrice).toFixed(2),
        }));
      }
    }
    // If user is editing total_price directly, don't overwrite it
  };

  const handleAddItem = async () => {
    if (!newItem.item_name || !newItem.quantity || !newItem.unit_price) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      await apiService.addBillItem(billId, newItem);
      setNewItem({
        item_name: "",
        quantity: "",
        unit_price: "",
        total_price: "",
        category: "",
      });
      setShowAddItem(false);
      await fetchBillDetails();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message || "Failed to add item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await apiService.deleteBillItem(billId, itemId);
      await fetchBillDetails();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message || "Failed to delete item");
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      // Update bill information
      await apiService.updateBill(billId, {
        merchant_name: editedBill.merchant_name,
        merchant_address: editedBill.merchant_address,
        bill_date: editedBill.bill_date,
        bill_time: editedBill.bill_time,
        total_amount: parseFloat(editedBill.total_amount),
        tax_amount: parseFloat(editedBill.tax_amount),
        payment_method: editedBill.payment_method,
        category: editedBill.category,
      });

      // Update each modified item
      const originalItems = bill.items || [];
      for (let i = 0; i < editedItems.length; i++) {
        const editedItem = editedItems[i];
        const originalItem = originalItems[i];

        // Check if item was modified
        if (JSON.stringify(editedItem) !== JSON.stringify(originalItem)) {
          await apiService.updateBillItem(billId, editedItem.item_id, {
            item_name: editedItem.item_name,
            quantity: parseFloat(editedItem.quantity),
            unit_price: parseFloat(editedItem.unit_price),
            total_price: parseFloat(editedItem.total_price),
            category: editedItem.category,
          });
        }
      }

      setEditMode(false);
      await fetchBillDetails();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedBill(bill);
    setEditedItems(bill.items || []);
    setEditMode(false);
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this entire bill? This action cannot be undone.",
      )
    )
      return;

    try {
      await apiService.deleteBill(billId);
      if (onUpdate) onUpdate();
      if (onClose) onClose();
    } catch (err) {
      alert(err.message || "Failed to delete bill");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Filter items based on search query
  const filteredItems = editedItems.filter((item) =>
    item.item_name?.toLowerCase().includes(itemSearchQuery.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="bill-detail-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading bill details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bill-detail-error">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <p>{error}</p>
        <button onClick={fetchBillDetails} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (!bill) return null;

  return (
    <div className="bill-detail-container">
      <div className="bill-detail-header">
        <h2>Bill Details</h2>
        <div className="header-actions">
          {!editMode ? (
            <>
              <button onClick={() => setEditMode(true)} className="btn-edit">
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
              <button onClick={handleDelete} className="btn-delete">
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-save"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} /> Save
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="btn-cancel"
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </>
          )}
          {onClose && (
            <button onClick={onClose} className="btn-close">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area with Image on Left and Details on Right */}
      <div className="bill-detail-content">
        {/* Bill Images - Left Side */}
        {bill.images && bill.images.length > 0 && (
          <div className="bill-images-section">
            <div className="bill-images-header">
              <h3>Bill Images</h3>
              <button
                className="toggle-images-btn"
                onClick={() => setShowImages(!showImages)}
                title={showImages ? "Hide Images" : "Show Images"}
              >
                <FontAwesomeIcon icon={showImages ? faTimes : faSearch} />
                <span>{showImages ? "Hide" : "Show"}</span>
              </button>
            </div>
            {showImages && (
              <>
                {bill.images.length > 1 && (
                  <div className="image-page-tabs">
                    {bill.images.map((image, index) => (
                      <button
                        key={image.image_id}
                        className={`image-page-tab ${activeImageIndex === index ? "active" : ""}`}
                        onClick={() => setActiveImageIndex(index)}
                      >
                        Page {image.page_number}
                      </button>
                    ))}
                  </div>
                )}
                <div className="bill-images-grid">
                  {(() => {
                    const image = bill.images[activeImageIndex];
                    const index = activeImageIndex;
                    return (
                      <div key={image.image_id} className="bill-image-item">
                        <div className="bill-image-viewer">
                          <div
                            className="bill-image-scroll-area"
                            ref={(el) =>
                              (wrapperRefs.current[image.image_id] = el)
                            }
                          >
                            <img
                              src={image.image_path}
                              alt={`Bill page ${index + 1}`}
                              draggable={false}
                              className="bill-zoomable-img"
                              style={{
                                transform: `scale(${getZoom(image.image_id)})`,
                              }}
                            />
                          </div>
                          <div className="img-toolbar">
                            <button
                              className="img-tool-btn"
                              onClick={() => zoomIn(image.image_id)}
                              title="Zoom In"
                            >
                              +
                            </button>
                            <button
                              className="img-tool-btn"
                              onClick={() => zoomOut(image.image_id)}
                              title="Zoom Out"
                            >
                              −
                            </button>
                            <span className="img-tool-pct">
                              {Math.round(getZoom(image.image_id) * 100)}%
                            </span>
                            <button
                              className="img-tool-btn"
                              onClick={() => panImage(image.image_id, -80, 0)}
                              title="Pan Left"
                            >
                              ◀
                            </button>
                            <button
                              className="img-tool-btn"
                              onClick={() => panImage(image.image_id, 80, 0)}
                              title="Pan Right"
                            >
                              ▶
                            </button>
                            <button
                              className="img-tool-btn"
                              onClick={() => panImage(image.image_id, 0, -80)}
                              title="Pan Up"
                            >
                              ▲
                            </button>
                            <button
                              className="img-tool-btn"
                              onClick={() => panImage(image.image_id, 0, 80)}
                              title="Pan Down"
                            >
                              ▼
                            </button>
                            <button
                              className="img-tool-btn img-tool-reset"
                              onClick={() => zoomReset(image.image_id)}
                              title="Reset"
                            >
                              ↺
                            </button>
                          </div>
                        </div>
                        <p>
                          Page {image.page_number}
                          {bill.images.length > 1 &&
                            ` of ${bill.images.length}`}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        {/* Bill Details - Right Side */}
        <div className="bill-details-wrapper">
          {/* Bill Information */}
          <div className="bill-info-section">
            <h3>Bill Information</h3>
            <div className="bill-info-grid">
              <div className="info-field">
                <label>Merchant Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedBill.merchant_name || ""}
                    onChange={(e) =>
                      handleBillFieldChange("merchant_name", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.merchant_name || "N/A"}</p>
                )}
              </div>

              <div className="info-field">
                <label>Merchant Address</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedBill.merchant_address || ""}
                    onChange={(e) =>
                      handleBillFieldChange("merchant_address", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.merchant_address || "N/A"}</p>
                )}
              </div>

              <div className="info-field">
                <label>Bill Date</label>
                {editMode ? (
                  <input
                    type="date"
                    value={editedBill.bill_date || ""}
                    onChange={(e) =>
                      handleBillFieldChange("bill_date", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.bill_date || "N/A"}</p>
                )}
              </div>

              <div className="info-field">
                <label>Bill Time</label>
                {editMode ? (
                  <input
                    type="time"
                    value={editedBill.bill_time || ""}
                    onChange={(e) =>
                      handleBillFieldChange("bill_time", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.bill_time || "N/A"}</p>
                )}
              </div>

              <div className="info-field">
                <label>Total Amount</label>
                {editMode ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editedBill.total_amount || ""}
                    onChange={(e) =>
                      handleBillFieldChange("total_amount", e.target.value)
                    }
                    className="total-amount-input"
                  />
                ) : (
                  <p className="amount-highlight">
                    {formatCurrency(bill.total_amount)}
                  </p>
                )}
              </div>

              <div className="info-field">
                <label>Tax Amount</label>
                {editMode ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editedBill.tax_amount || ""}
                    onChange={(e) =>
                      handleBillFieldChange("tax_amount", e.target.value)
                    }
                  />
                ) : (
                  <p>{formatCurrency(bill.tax_amount)}</p>
                )}
              </div>

              <div className="info-field">
                <label>Payment Method</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedBill.payment_method || ""}
                    onChange={(e) =>
                      handleBillFieldChange("payment_method", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.payment_method || "N/A"}</p>
                )}
              </div>

              <div className="info-field">
                <label>Category</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editedBill.category || ""}
                    onChange={(e) =>
                      handleBillFieldChange("category", e.target.value)
                    }
                  />
                ) : (
                  <p>{bill.category || "N/A"}</p>
                )}
              </div>
            </div>
          </div>

          {/* Bill Items */}
          <div className="bill-items-section">
            <div className="items-header">
              <h3>Bill Items</h3>
              <button
                onClick={() => setShowAddItem(!showAddItem)}
                className="btn-add-item"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Item
              </button>
            </div>

            {/* Search Box for Items */}
            <div className="items-search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search items by name..."
                value={itemSearchQuery}
                onChange={(e) => setItemSearchQuery(e.target.value)}
                className="items-search-input"
              />
              {itemSearchQuery && (
                <button
                  onClick={() => setItemSearchQuery("")}
                  className="clear-search-btn"
                  title="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            {/* Add New Item Form */}
            {showAddItem && (
              <div className="add-item-form">
                <h4>Add New Item</h4>
                <div className="item-form-grid">
                  <input
                    type="text"
                    placeholder="Item Name *"
                    value={newItem.item_name}
                    onChange={(e) =>
                      handleNewItemChange("item_name", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Quantity *"
                    value={newItem.quantity}
                    onChange={(e) =>
                      handleNewItemChange("quantity", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Unit Price *"
                    value={newItem.unit_price}
                    onChange={(e) =>
                      handleNewItemChange("unit_price", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Total Price"
                    value={newItem.total_price}
                    onChange={(e) =>
                      handleNewItemChange("total_price", e.target.value)
                    }
                    className="editable-total-input"
                  />
                  <input
                    type="text"
                    placeholder="Category"
                    value={newItem.category}
                    onChange={(e) =>
                      handleNewItemChange("category", e.target.value)
                    }
                  />
                </div>
                <div className="item-form-actions">
                  <button onClick={handleAddItem} className="btn-save-item">
                    <FontAwesomeIcon icon={faCheck} /> Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddItem(false);
                      setNewItem({
                        item_name: "",
                        quantity: "",
                        unit_price: "",
                        total_price: "",
                        category: "",
                      });
                    }}
                    className="btn-cancel-item"
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Items Table */}
            {filteredItems && filteredItems.length > 0 ? (
              <div className="items-table-container">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                      <th>Category</th>
                      {editMode && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const realIndex = editedItems.findIndex(
                        (ei) => ei.item_id === item.item_id,
                      );
                      return (
                        <tr key={item.item_id}>
                          <td>
                            {editMode ? (
                              <input
                                type="text"
                                value={item.item_name || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    realIndex,
                                    "item_name",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.item_name
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                step="0.01"
                                value={item.quantity || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    realIndex,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.quantity
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                step="0.01"
                                value={item.unit_price || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    realIndex,
                                    "unit_price",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              formatCurrency(item.unit_price)
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                step="0.01"
                                value={item.total_price || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    realIndex,
                                    "total_price",
                                    e.target.value,
                                  )
                                }
                                className="editable-total"
                              />
                            ) : (
                              formatCurrency(item.total_price)
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="text"
                                value={item.category || ""}
                                onChange={(e) =>
                                  handleItemFieldChange(
                                    realIndex,
                                    "category",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.category || "N/A"
                            )}
                          </td>
                          {editMode && (
                            <td>
                              <button
                                onClick={() => handleDeleteItem(item.item_id)}
                                className="btn-delete-item"
                                title="Delete item"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="items-footer-row">
                      <td colSpan={editMode ? 3 : 3}>
                        <strong>
                          {filteredItems.length} item
                          {filteredItems.length !== 1 ? "s" : ""}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {formatCurrency(
                            filteredItems.reduce(
                              (sum, item) =>
                                sum + (parseFloat(item.total_price) || 0),
                              0,
                            ),
                          )}
                        </strong>
                      </td>
                      <td colSpan={editMode ? 2 : 1}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="no-items">
                <FontAwesomeIcon icon={faInbox} />
                <p>
                  {itemSearchQuery
                    ? `No items found matching "${itemSearchQuery}"`
                    : "No items found"}
                </p>
                {itemSearchQuery && (
                  <button
                    onClick={() => setItemSearchQuery("")}
                    className="btn-clear-search"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetail;
